import { useContext, useEffect, useState } from 'react';
import type { SavedReply, SavedReplyModal_Zrm_GetSavedRepliesQuery } from '../types/GraphqlTypes';
import {
  SavedReplyModal_Zrm_GetSavedRepliesDocument,
  useSavedReplyModal_Zrm_AddSavedRepliesMutation,
  useSavedReplyModal_Zrm_DeleteSavedRepliesMutation,
  useSavedReplyModal_Zrm_EditSavedReplyMutation,
  useSavedReplyModal_Zrm_GetSavedRepliesQuery,
} from '../types/GraphqlTypes';
import { Toast, useToast } from '@zillow/constellation-10';
import useErrorHelpers from '../hooks/useErrorHelpers';
import { logApolloError } from '../utils/errorUtils';
import {
  emitCustomReplyCreatedEvent,
  emitCustomReplyEditedEvent,
  emitDefaultReplyEditedEvent,
} from '../constants/clickstreamEvents/savedReplyModal';
import isDefaultSavedReply from '../utils/isDefaultSavedReply';
import { gql, useQuery } from '@apollo/client';
import { inboxPageCreateSavedReplyTextVariable } from '../lib/apollo';
import { useAuth } from '@zg-rentals/auth-comet';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';

type Modes = 'create' | 'edit' | 'view';
const MAX_RETRY_ATTEMPTS = 3;
export const MAX_SAVED_REPLIES_COUNT = 20;

export const useSavedReplies = () => {
  const { listingAlias } = useContext(InboxPageContext);
  const { enqueueToast } = useToast();
  const { formatError } = useErrorHelpers();
  const { user } = useAuth();

  const [savedReplies, setSavedReplies] = useState<Array<SavedReply | null>>([]);

  const { data: savedReplyText } = useQuery(GET_SAVED_REPLY_TEXT, { fetchPolicy: 'cache-only' });
  const { inboxPageCreateSavedReplyText } = savedReplyText || {};

  const [mode, setMode] = useState<Modes>('view');
  const [hasFormError, setHasFormError] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);

  const [selectedSavedReply, setSelectedSavedReply] = useState<SavedReply>({
    dateUpdatedMs: '',
    reply: '',
    replyIndex: 0,
    title: '',
  });

  // Tracks changes when a saved reply is being edited
  const [editedSavedReply, setEditedSavedReply] = useState<SavedReply>({
    dateUpdatedMs: '',
    reply: '',
    replyIndex: 0,
    title: '',
  });

  useEffect(() => {
    if (inboxPageCreateSavedReplyText) {
      setEditedSavedReply({
        dateUpdatedMs: '',
        reply: inboxPageCreateSavedReplyText,
        replyIndex: 0,
        title: '',
      });
      setMode('create');
    }
  }, [inboxPageCreateSavedReplyText]);

  const {
    data: savedReplyData,
    loading: isLoadingSavedReplies,
    error: hasErrorLoadingSavedReplies,
    refetch: refetchSavedReplyData,
  } = useSavedReplyModal_Zrm_GetSavedRepliesQuery({
    fetchPolicy: 'cache-first',
    onCompleted(response) {
      const customReplies = response.ZRM_getSavedReplies.customReplies;
      const defaultReplies = response.ZRM_getSavedReplies.defaultSavedReplies;
      const allReplies = [...customReplies, ...defaultReplies];
      setSavedReplies(allReplies);

      if (allReplies[0]) {
        setSelectedSavedReply(allReplies[0]);
      }
    },
    onError(err) {
      logApolloError(err, {
        details: 'Saved reply modal - Error fetching saved replies',
        retryAttempts,
        ...formatError(err),
      });

      if (retryAttempts < MAX_RETRY_ATTEMPTS) {
        setRetryAttempts(retryAttempts + 1);
        refetchSavedReplyData();
      }
    },
  });

  // Check if we're trying to add text from MessageToolbar but have reached the limit
  const hasTextFromToolbar = editedSavedReply.reply && mode === 'create';
  const hasReachedMaxCustomRepliesLimit =
    (savedReplyData?.ZRM_getSavedReplies.customReplies || []).length >= MAX_SAVED_REPLIES_COUNT;
  const shouldShowLimitError = hasReachedMaxCustomRepliesLimit && hasTextFromToolbar;

  // Check if the current title already exists in saved replies (excluding the current reply being edited)
  const hasDuplicateTitle = (title: string): boolean => {
    if (!title.trim()) return false;

    return savedReplies.some((reply) => {
      if (!reply) return false;

      // When editing, exclude the current reply from duplicate check
      if (mode === 'edit' && reply.replyIndex === editedSavedReply.replyIndex) {
        return false;
      }

      return reply.title.toLowerCase().trim() === title.toLowerCase().trim();
    });
  };

  // Auto-switch to view mode if limit is reached and user is trying to create
  useEffect(() => {
    if (shouldShowLimitError) {
      setMode('view');
    }
  }, [shouldShowLimitError]);

  const [Zrm_addSavedReplies, { loading: isSavingNewReply }] = useSavedReplyModal_Zrm_AddSavedRepliesMutation({
    onCompleted() {
      if (mode === 'edit') {
        emitDefaultReplyEditedEvent(user, { listingAlias });
      } else if (mode === 'create') {
        emitCustomReplyCreatedEvent(user, { listingAlias });
      }

      refetchSavedReplyData();
      setMode('view');
      enqueueToast(<Toast tone="success">Reply added</Toast>);
    },
    onError(err) {
      setHasFormError(true);
      logApolloError(err, {
        details: 'Saved reply modal - Error adding saved replies',
        mode,
        ...formatError(err),
      });
    },
  });

  const [Zrm_deleteSavedReplies, { loading: isDeletingReply }] = useSavedReplyModal_Zrm_DeleteSavedRepliesMutation({
    onCompleted() {
      enqueueToast(<Toast tone="success">Reply deleted</Toast>);
    },
    onError(err) {
      enqueueToast(
        <Toast tone="critical">There was an error deleting the saved reply. Please refresh and try again.</Toast>,
      );
      logApolloError(err, {
        details: 'Saved reply modal - Error deleting saved reply',
        ...selectedSavedReply,
        ...formatError(err),
      });
    },
    update(cache) {
      const { reply, replyIndex, title } = selectedSavedReply;

      // Evict the individual SavedReply
      cache.evict({
        id: cache.identify({
          __typename: 'SavedReply',
          replyIndex,
          reply,
          title,
        }),
      });

      // Update the parent query
      cache.updateQuery<SavedReplyModal_Zrm_GetSavedRepliesQuery>(
        { query: SavedReplyModal_Zrm_GetSavedRepliesDocument },
        (data) => {
          if (!data) return data;

          return {
            ZRM_getSavedReplies: {
              ...data.ZRM_getSavedReplies,
              customReplies: data.ZRM_getSavedReplies.customReplies.filter((reply) => reply?.replyIndex !== replyIndex),
            },
          };
        },
      );

      cache.gc();
    },
  });

  const [Zrm_editSavedReply, { loading: isSavingEditedReply }] = useSavedReplyModal_Zrm_EditSavedReplyMutation({
    onCompleted() {
      emitCustomReplyEditedEvent(user, { listingAlias });

      refetchSavedReplyData();
      setMode('view');
      enqueueToast(<Toast tone="success">Reply saved</Toast>);
    },
    onError(err) {
      setHasFormError(true);
      logApolloError(err, {
        details: 'Saved reply modal - Error editing saved reply',
        ...selectedSavedReply,
        ...formatError(err),
      });
    },
  });

  const handleDelete = (replyIndex: number): void => {
    Zrm_deleteSavedReplies({
      variables: {
        savedReplyIndices: [replyIndex],
      },
    });
  };

  const handleEdit = (replyIndex: number): void => {
    setHasFormError(false);
    const selectedSavedReply = savedReplies.find((reply) => reply?.replyIndex === replyIndex);
    if (selectedSavedReply) {
      setEditedSavedReply(selectedSavedReply);
      setMode('edit');
    }
  };

  const handleSelect = (replyIndex: number): void => {
    const selectedSavedReply = savedReplies.find((reply) => reply?.replyIndex === replyIndex);
    if (selectedSavedReply) {
      setSelectedSavedReply(selectedSavedReply);
    }
  };

  const handleCreate = (): void => {
    const emptySavedReply: SavedReply = {
      title: '',
      reply: '',
      replyIndex: 0,
    };

    setEditedSavedReply(emptySavedReply);
    setMode('create');
  };

  const handleSave = (): void => {
    const { title, reply, replyIndex } = editedSavedReply;
    const replyToSave = {
      title: title.trim(),
      reply: reply.trim(),
      replyIndex: replyIndex,
    };

    // Prevent any updates if there are no changes to save
    if (replyToSave.title === selectedSavedReply.title && replyToSave.reply === selectedSavedReply.reply) {
      return handleCancel();
    }

    inboxPageCreateSavedReplyTextVariable('');

    // Default replies get added as a new reply when edited & saved
    if (mode === 'create' || isDefaultSavedReply(replyToSave)) {
      Zrm_addSavedReplies({
        variables: {
          savedReplies: [replyToSave],
        },
      });
    } else {
      Zrm_editSavedReply({
        variables: {
          savedReply: replyToSave,
        },
      });
    }
  };

  const handleCancel = (): void => {
    setMode('view');
    inboxPageCreateSavedReplyTextVariable('');
  };

  return {
    editedSavedReply,
    handleCancel,
    handleCreate,
    handleDelete,
    handleEdit,
    handleSave,
    handleSelect,
    hasErrorLoadingSavedReplies: !!hasErrorLoadingSavedReplies,
    hasFormError,
    hasReachedMaxCustomRepliesLimit,
    hasDuplicateTitle,
    isDeletingReply,
    isLoadingSavedReplies,
    isSavingReply: isSavingEditedReply || isSavingNewReply,
    mode,
    savedReplies,
    selectedSavedReply,
    setEditedSavedReply,
  };
};

const GET_SAVED_REPLY_TEXT = gql`
  query {
    inboxPageCreateSavedReplyText @client
  }
`;
